import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { WorklistService } from '../worklist.service';
import { AppMessageService } from '../../../shared/services/app-message/app-message.service';
import { ParameterService } from '../../../shared/services/parameter/parameter.service';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-work-detail',
  templateUrl: './work-status-detail.component.html',
  styleUrls: ['../worklist-update/worklist-update.component.scss'],
})
export class WorkDetailComponent implements OnInit {
  @Input() visible = true;
  @Output() visibleChange = new EventEmitter<boolean>();

  work: any;
  workId!: number;

  teamsOptions: any[] = [];
  priorityOptions: any[] = [];
  statusOptions: any[] = [];

  @Output() workUpdated = new EventEmitter<any>();

  @Input() disabled?: boolean = false;

  form: FormGroup;

  statusLogsVisible = false;
  statusLogs: any[] = [];

  minWorkDate: Date | null = null;

  @Input() triggerStatusLogs? = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly service: WorklistService,
    private readonly messageService: AppMessageService,
    private readonly parameterService: ParameterService,
    private readonly route: ActivatedRoute
  ) {
    this.form = this.fb.group(
      {
        title: ['', Validators.required],
        description: ['', Validators.required],
        workDate: [null, Validators.required],
        alarmClokDate: [null, Validators.required],
        priority: [null],
        teams: [[], Validators.required],
        status: [null],
      },
      {
        validators: [workDateAfterAlarmTimeValidator()],
      }
    );
  }

  ngOnInit(): void {
    this.visible = true;
    this.loadParameterOptions();

    this.workId = Number(this.route.snapshot.paramMap.get('id'));

    if (this.workId) {
      this.getWorkDetail();
    }
  }

  getWorkDetail(): void {
    this.service.find(this.workId).subscribe({
      next: (response: any) => {
        const data = response?.data ?? response;

        this.work = {
          ...data,
          workDate: this.toDate(data?.workDate),
          alarmClokDate: this.toDate(
            data?.alarmClokDate ??
            data?.alarmClockDate ??
            data?.alarmTime
          ),
        };

        this.patchWorkToForm();
      },
      error: (err) => {
        const message =
          err?.error?.message ??
          err?.message ??
          'İş detayı alınamadı';

        this.messageService.errorToast('', message);
      },
    });
  }

  private patchWorkToForm(): void {
    if (!this.work) return;

    this.form.patchValue({
      title: this.work.title ?? '',
      description: this.work.description ?? '',
      workDate: this.toDate(this.work.workDate),
      alarmClokDate: this.toDate(
        this.work.alarmClokDate ??
        this.work.alarmClockDate ??
        this.work.alarmTime
      ),
      priority: this.work.priority ?? this.work.priorityId ?? null,
      teams: this.work.teams ?? this.work.teamIds ?? [],
      status: this.work.status ?? null,
    });

    const alarmDate = this.form.get('alarmClokDate')?.value;

    if (alarmDate) {
      this.minWorkDate = alarmDate;
    }
  }

  private toDate(value: any): Date | null {
    if (!value) return null;

    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }

    const date = new Date(value);

    return isNaN(date.getTime()) ? null : date;
  }

  loadParameterOptions(): void {
    this.parameterService
      .getParameterValues('data-group', 'teams')
      .subscribe((res) => {
        this.teamsOptions = res?.datas?.values?.map((data: any) => ({
          label: data.valueObject.name.value,
          value: data?.valueObject.id.value,
        })) ?? [];
      });

    this.parameterService
      .getParameterValues('data-group', 'statuses')
      .subscribe((res) => {
        this.statusOptions = res?.datas?.values?.map((data: any) => ({
          label: data.valueObject.name.value,
          value: data?.valueObject.id.value,
        })) ?? [];
      });

    this.parameterService
      .getParameterValues('data-group', 'priority')
      .subscribe((res) => {
        this.priorityOptions = res?.datas?.values?.map((data: any) => ({
          label: data.valueObject.name.value,
          value: data?.valueObject.id.value,
        })) ?? [];
      });
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const formValue = this.form.value;

    const updatedData = {
      ...this.work,
      ...formValue,
      workDate: formValue.workDate,
      alarmClokDate: formValue.alarmClokDate,
    };

    this.service.update(this.work.id, updatedData).subscribe({
      next: (response: any) => {
        this.workUpdated.emit(response.data);
        this.messageService.successToast('', response.message);

        this.visibleChange.emit(false);
        window.close();
      },
      error: (err) => {
        const message =
          err?.error?.message ??
          err?.message ??
          '';

        this.messageService.errorToast('', message);
      },
    });
  }

  cancel(): void {
    this.visibleChange.emit(false);
    this.form.reset();
    window.close();
  }

  openStatusLogsDialog(): void {
    if (!this.work?.id) return;

    this.service.getStatusLogs(this.work.id).subscribe({
      next: (res) => {
        this.statusLogs = res?.data?.statusLogs ?? [];

        this.statusLogsVisible = this.statusLogs.length > 0;
        this.visible = false;

        this.statusLogs.length > 0
          ? this.messageService.successToast('', res.message)
          : this.messageService.errorToast('', res.message);
      },
      error: (err) => {
        const message =
          err?.error?.message ??
          err?.message ??
          '';

        this.messageService.errorToast('', message);
      },
    });
  }

  closeStatusLogsDialog(): void {
    this.statusLogsVisible = false;
    this.statusLogs = [];
    this.visible = true;
  }

  onAlarmTimeChange(): void {
    const alarmTime = this.form.get('alarmClokDate')?.value;

    if (alarmTime) {
      this.minWorkDate = new Date(alarmTime);
    }
  }

  get workDateBeforeAlarmTimeError(): boolean {
    return !!(
      this.form.errors?.['workBeforeAlarm'] &&
      (
        this.form.get('workDate')?.touched ||
        this.form.get('alarmClokDate')?.touched
      )
    );
  }
}

export function workDateAfterAlarmTimeValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const workDate = control.get('workDate')?.value;
    const alarmTime = control.get('alarmClokDate')?.value;

    if (workDate && alarmTime) {
      const workDateTime = new Date(workDate).getTime();
      const alarmDateTime = new Date(alarmTime).getTime();

      if (workDateTime < alarmDateTime) {
        return { workBeforeAlarm: true };
      }
    }

    return null;
  };
}